import { apiClient } from '@features/shared/api/client'
import type {
  DefaultSenderRequest,
  AddEmailUnsubscribeRequest,
  AddSendingAddressRequest,
  AddSendingDomainRequest,
  DefaultReplyToRequest,
  EmailAddress,
  EmailAddressesResponse,
  EmailUnsubscribesResponse,
  SendingAddressesResponse,
  SendingDomain,
  SendingDomainsResponse,
} from './contracts'

export const emailQueryKeys = {
  all: ['email'] as const,
  domains: ['email', 'domains'] as const,
  sendingAddresses: ['email', 'sending-addresses'] as const,
  addresses: ['email', 'addresses'] as const,
  unsubscribes: ['email', 'unsubscribes'] as const,
}

const DOMAINS_PATH = '/settings/email/domains'

export async function getSendingDomains(signal?: AbortSignal): Promise<SendingDomainsResponse> {
  return (await apiClient.get<SendingDomainsResponse>(DOMAINS_PATH, { signal })).data
}

export async function addSendingDomain(request: AddSendingDomainRequest): Promise<SendingDomain> {
  return (await apiClient.post<SendingDomain>(DOMAINS_PATH, request)).data
}

/** Asks the email provider to look for the domain's records now. */
export async function checkSendingDomain(sendingDomainId: string): Promise<SendingDomain> {
  return (await apiClient.post<SendingDomain>(`${DOMAINS_PATH}/${sendingDomainId}/check`)).data
}

export async function removeSendingDomain(sendingDomainId: string): Promise<void> {
  await apiClient.delete(`${DOMAINS_PATH}/${sendingDomainId}`)
}

export async function getSendingAddresses(signal?: AbortSignal): Promise<SendingAddressesResponse> {
  return (await apiClient.get<SendingAddressesResponse>('/email/sending-addresses', { signal }))
    .data
}

const ADDRESSES_PATH = '/settings/email/addresses'

export async function getEmailAddresses(signal?: AbortSignal): Promise<EmailAddressesResponse> {
  return (await apiClient.get<EmailAddressesResponse>(ADDRESSES_PATH, { signal })).data
}

/** Adds a sending address; one the person can't already send from gets a link in its inbox. */
export async function addSendingAddress(request: AddSendingAddressRequest): Promise<EmailAddress> {
  return (await apiClient.post<EmailAddress>(ADDRESSES_PATH, request)).data
}

/** Emails the address a new confirmation link; the last one stops working. */
export async function resendAddressConfirmation(addressId: string): Promise<EmailAddress> {
  return (await apiClient.post<EmailAddress>(`${ADDRESSES_PATH}/${addressId}/resend`)).data
}

export async function removeSendingAddress(addressId: string): Promise<void> {
  await apiClient.delete(`${ADDRESSES_PATH}/${addressId}`)
}

export async function setDefaultSender(
  request: DefaultSenderRequest
): Promise<EmailAddressesResponse> {
  return (await apiClient.put<EmailAddressesResponse>('/settings/email/default-sender', request))
    .data
}

export async function setDefaultReplyTo(
  request: DefaultReplyToRequest
): Promise<EmailAddressesResponse> {
  return (await apiClient.put<EmailAddressesResponse>('/settings/email/default-reply-to', request))
    .data
}

const UNSUBSCRIBES_PATH = '/settings/email/unsubscribes'

/** Everyone who stopped promotional email, newest first. */
export async function getEmailUnsubscribes(
  signal?: AbortSignal
): Promise<EmailUnsubscribesResponse> {
  return (await apiClient.get<EmailUnsubscribesResponse>(UNSUBSCRIBES_PATH, { signal })).data
}

/** Stops all promotional email to an address; the list as it is now. */
export async function addEmailUnsubscribe(
  request: AddEmailUnsubscribeRequest
): Promise<EmailUnsubscribesResponse> {
  return (await apiClient.post<EmailUnsubscribesResponse>(UNSUBSCRIBES_PATH, request)).data
}

/** Lets promotional email reach an address again; the list as it is now. */
export async function removeEmailUnsubscribe(
  unsubscribeId: string
): Promise<EmailUnsubscribesResponse> {
  return (
    await apiClient.delete<EmailUnsubscribesResponse>(`${UNSUBSCRIBES_PATH}/${unsubscribeId}`)
  ).data
}
